import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';

interface FlowStep {
  num: string;
  head: string;
  desc: string;
}

interface ForgotPasswordPromoPaneProps {
  activeIndex?: number;
}

export function ForgotPasswordPromoPane({ activeIndex = 0 }: ForgotPasswordPromoPaneProps) {
  const { t } = useTranslation();
  const steps = t('auth.forgot.promo.steps', { returnObjects: true }) as FlowStep[];

  return (
    <div className="relative flex w-full flex-col items-start overflow-hidden bg-accent p-16 pl-14 text-fg-inv">
      <div className="text-[12px] font-semibold uppercase tracking-[0.18em] opacity-70">
        {t('auth.forgot.promo.eyebrow')}
      </div>

      <div className="mt-8 max-w-[480px] font-serif text-[56px] font-medium leading-[1.05] tracking-[-0.02em]">
        {t('auth.forgot.promo.quotePart1')}{' '}
        <em className="font-medium italic">{t('auth.forgot.promo.quotePart1Em')}</em>{' '}
        {t('auth.forgot.promo.quotePart1Tail')}
        <br />
        {t('auth.forgot.promo.quotePart2')}{' '}
        <em className="font-medium italic">{t('auth.forgot.promo.quotePart2Em')}</em>
        {t('auth.forgot.promo.quotePart2Tail')}
      </div>

      <div className="mt-12 flex w-full max-w-[420px] flex-col gap-3.5">
        {steps.map((step, i) => {
          const active = i === activeIndex;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.42, delay: 0.18 + i * 0.08, ease: 'easeOut' }}
              className={cn(
                'flex items-start gap-3.5 rounded-2xl border px-[18px] py-4 transition-colors duration-300',
                active
                  ? 'border-fg-inv bg-fg-inv text-accent'
                  : 'border-fg-inv/15 bg-fg-inv/5 text-fg-inv',
              )}
            >
              <div
                className={cn(
                  'inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg font-mono text-xs font-medium',
                  active ? 'bg-accent text-fg-inv' : 'bg-fg-inv/15 text-fg-inv',
                )}
              >
                {step.num}
              </div>
              <div className="flex-1">
                <div className="font-serif text-[18px] font-medium leading-tight tracking-[-0.01em]">{step.head}</div>
                <div
                  className={cn(
                    'mt-1 text-[14px] leading-snug',
                    active ? 'text-fg/70' : 'text-fg-inv/65',
                  )}
                >
                  {step.desc}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="absolute bottom-12 left-14 font-mono text-[12px] font-medium tracking-[0.04em] opacity-60">
        {t('auth.forgot.promo.foot')}
      </div>
    </div>
  );
}
